import React, { useEffect, useState } from 'react';
import { FREE_API, formatDate, inputClass, labelClass } from './api';

async function adminFetch(password) {
  const res = await fetch(`${FREE_API}/admin/users`, {
    headers: { 'X-Admin-Password': password },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Request failed');
  return data;
}

const csvEscape = (v) => {
  const s = String(v ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function downloadCsv(users) {
  const header = ['Email', 'Date Joined', 'Sessions Logged', 'Last Session', '6-Session Trigger Hit'];
  const rows = users.map((u) => [
    u.email,
    u.date_joined ? formatDate(u.date_joined) : '',
    u.session_count,
    u.last_session_date ? formatDate(u.last_session_date) : '',
    u.six_trigger_shown ? 'yes' : 'no',
  ]);
  const csv = [header, ...rows].map((r) => r.map(csvEscape).join(',')).join('\n');
  saveBlob(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), 'coachrands-waitlist.csv');
}

// Temporary one-off export of the paid conversations. Remove with its endpoint once downloaded.
async function downloadConversations() {
  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/conversations/export`, {
    headers: { 'X-Admin-Password': sessionStorage.getItem('admin_password') || '' },
  });
  if (!res.ok) throw new Error('Export failed. Unlock the admin page again and retry.');
  saveBlob(await res.blob(), 'coachrands-paid-conversations.json');
}

export default function AdminWaitlist() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [unlocked, setUnlocked] = useState(false);
  const [checking, setChecking] = useState(false);
  const [exportError, setExportError] = useState('');

  useEffect(() => {
    const saved = sessionStorage.getItem('admin_password');
    if (saved) {
      setChecking(true);
      adminFetch(saved)
        .then((d) => { setData(d); setUnlocked(true); })
        .catch(() => sessionStorage.removeItem('admin_password'))
        .finally(() => setChecking(false));
    }
  }, []);

  const handleUnlock = async (e) => {
    e.preventDefault();
    setError('');
    setChecking(true);
    try {
      const d = await adminFetch(password);
      sessionStorage.setItem('admin_password', password);
      setData(d);
      setUnlocked(true);
    } catch (err) {
      setError(err.message === 'Invalid admin password' ? 'Wrong password. Try again.' : err.message);
    } finally {
      setChecking(false);
    }
  };

  if (!unlocked) {
    return (
      <div className="min-h-screen bg-mono-base flex items-center justify-center px-4">
        <form onSubmit={handleUnlock} className="w-full max-w-sm border border-mono-line bg-mono-card p-8" data-testid="admin-login-form">
          <span className="text-mono-mute font-heading text-xs font-bold uppercase tracking-[0.25em]">
            Coach Rands Method — Admin
          </span>
          <h1 className="font-heading text-3xl font-black uppercase tracking-tight text-white leading-none mt-3 mb-6">
            Restricted
          </h1>
          <label className={labelClass}>Admin password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={inputClass}
            placeholder="Enter password"
            autoFocus
            data-testid="admin-password-input"
          />
          {error && <p className="text-red-400 font-body text-sm mt-3" data-testid="admin-login-error">{error}</p>}
          <button
            type="submit"
            disabled={checking || !password}
            className="w-full mt-5 bg-white hover:bg-[#E5E5E5] disabled:opacity-40 disabled:cursor-not-allowed text-black font-heading text-sm font-bold uppercase tracking-[0.15em] py-3 transition-colors"
            data-testid="admin-login-submit"
          >
            {checking ? 'Checking...' : 'Unlock'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-mono-base">
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-10 md:py-14" data-testid="admin-page">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="text-mono-mute font-heading text-xs font-bold uppercase tracking-[0.25em]">
              Coach Rands Method — Admin
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tight text-white leading-none mt-4 mb-2">
              Waitlist
            </h1>
            {data && (
              <p className="text-mono-mute font-body text-sm" data-testid="admin-total-users">
                {data.total} free tier {data.total === 1 ? 'user' : 'users'}
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => downloadConversations().catch((err) => setExportError(err.message))}
              className="border border-white text-white hover:bg-white hover:text-black font-heading text-xs font-bold uppercase tracking-[0.15em] px-5 py-3 transition-colors"
              data-testid="admin-export-conversations-button"
            >
              Export paid conversations
            </button>
            {data && data.users.length > 0 && (
              <button
                onClick={() => downloadCsv(data.users)}
                className="bg-white hover:bg-[#E5E5E5] text-black font-heading text-xs font-bold uppercase tracking-[0.15em] px-5 py-3 transition-colors"
                data-testid="admin-export-csv-button"
              >
                Export waitlist
              </button>
            )}
          </div>
        </div>
        {exportError && <p className="text-red-400 font-body text-sm mt-3" data-testid="admin-export-error">{exportError}</p>}

        {!data && <p className="text-mono-mute font-body mt-8" data-testid="admin-loading">Loading...</p>}

        {data && data.users.length === 0 && (
          <p className="text-mono-mute font-body mt-8" data-testid="admin-empty">No free tier users yet.</p>
        )}

        {data && data.users.length > 0 && (
          <div className="overflow-x-auto border border-mono-line mt-8">
            <table className="w-full text-left font-body text-sm" data-testid="admin-users-table">
              <thead>
                <tr className="border-b border-mono-line bg-mono-surface">
                  {['Email', 'Date joined', 'Sessions', 'Last session', '6-session trigger'].map((h) => (
                    <th key={h} className="px-4 py-3 font-heading text-xs font-bold uppercase tracking-[0.15em] text-mono-mute whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.users.map((u) => (
                  <tr key={u.email} className="border-b border-mono-line/60 hover:bg-mono-card transition-colors" data-testid="admin-user-row">
                    <td className="px-4 py-3 text-white whitespace-nowrap">{u.email}</td>
                    <td className="px-4 py-3 text-white whitespace-nowrap">{formatDate(u.date_joined)}</td>
                    <td className="px-4 py-3 text-white">{u.session_count}</td>
                    <td className="px-4 py-3 text-white whitespace-nowrap">{u.last_session_date ? formatDate(u.last_session_date) : '—'}</td>
                    <td className="px-4 py-3">
                      {u.six_trigger_shown ? (
                        <span className="text-white font-heading text-xs font-bold uppercase tracking-[0.15em]">Hit</span>
                      ) : (
                        <span className="text-mono-mute/60">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
